import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import LdasApi from "../../../../apis/Ldas.api";
import ContentHeaderComponent from "../../../layout/ContentHeaderComponent";
import useDeleteLdasProduct from "../useDeleteLdasProduct";

import LdasProductFilterBar from "./LdasProductFilterBar";
import LdasProductTable from "./LdasProductTable";

const defaultPageSize = 10;

const LdasProductListComponent = () => {
  const history = useHistory();
  const [searchText, setSearchText] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [productListPage, setProductListPage] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: defaultPageSize },
    totalElements: 0,
  });
  const [loading, setLoading] = useState(false);
  const { deleteLdasProductWithConfirm } = useDeleteLdasProduct();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery((searchText || "").trim());
      setProductListPage((prev) => ({
        ...prev,
        pageable: { ...prev.pageable, pageNumber: 0 },
      }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LdasApi.readLdasProductList({
        query: debouncedQuery,
        page: productListPage.pageable.pageNumber,
        size: productListPage.pageable.pageSize,
      });
      // console.log("readLdasProductList res", res);
      setProductListPage((prev) => ({
        ...prev,
        content: res?.result?.items ?? [],
        pageable: {
          pageNumber: res?.result?.page ?? prev.pageable.pageNumber,
          pageSize: res?.result?.size ?? prev.pageable.pageSize,
        },
        totalElements: res?.result?.totalCount ?? 0,
      }));
    } catch (e) {
      console.error("readLdasProductList", e);
      setProductListPage((prev) => ({
        ...prev,
        content: [],
        totalElements: 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [
    debouncedQuery,
    productListPage.pageable.pageNumber,
    productListPage.pageable.pageSize,
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleTablePaginationChange = (nextPage, nextPageSize) => {
    setProductListPage((prev) => {
      const prevSize = prev.pageable.pageSize;
      const size = nextPageSize ?? prevSize;
      const sizeChanged = size !== prevSize;
      return {
        ...prev,
        pageable: {
          pageNumber: sizeChanged ? 0 : nextPage - 1,
          pageSize: size,
        },
      };
    });
  };

  const handleAddProduct = () => history.push("/ldas/create");
  const handleEditProduct = (id) => history.push(`/ldas/edit/${id}`);
  const handleDetailProduct = (id) => history.push(`/ldas/detail/${id}`);

  const handleDeleteProduct = useCallback(
    async (record) => {
      if (!record?.id) return;
      const { deleted } = await deleteLdasProductWithConfirm({
        productId: record.id,
        productName: record.productName,
        imageKey: record.imageUrl,
        redirectToList: false,
      });
      if (deleted) {
        void fetchProducts();
      }
    },
    [deleteLdasProductWithConfirm, fetchProducts],
  );

  return (
    <div className="ldas-page">
      <ContentHeaderComponent
        title="제품 관리"
        subtitle={`총 ${productListPage.totalElements}개 제품 등록됨`}
        actionButton={{ label: "+ 제품 추가", onClick: handleAddProduct }}
      />

      <div className="ldas-page__content">
        <LdasProductFilterBar
          searchText={searchText}
          onSearchChange={setSearchText}
        />

        <LdasProductTable
          dataSource={productListPage.content}
          loading={loading}
          onEdit={handleEditProduct}
          onDetail={handleDetailProduct}
          onDelete={handleDeleteProduct}
          pagination={{
            current: productListPage.pageable.pageNumber + 1,
            pageSize: productListPage.pageable.pageSize,
            total: productListPage.totalElements,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / 총 ${total}건`,
            hideOnSinglePage: false,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: handleTablePaginationChange,
            onShowSizeChange: handleTablePaginationChange,
          }}
        />
      </div>
    </div>
  );
};

export default LdasProductListComponent;
